import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useId,
  cloneElement,
  isValidElement,
  type ComponentProps,
  type ReactNode,
  type ReactElement,
  type HTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { HelpCircle } from "lucide-react";
import { cn } from "./utils";

// Cap derived aria-label length so very long tooltip copy doesn't produce a
// 400-character accessible name — screen readers would read the whole thing
// before the user can focus the next element. Module-level so the constant
// isn't re-allocated on every InfoTooltip render and so the value can be
// referenced by tests without reaching into the component body.
const MAX_ARIA_LABEL_LEN = 80;

interface TooltipProps {
  content: string | ReactNode;
  children: ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  delay?: number;
  disabled?: boolean;
  className?: string;
  maxWidth?: number;
  /**
   * Optional accessible name used as the aria-label on the trigger when
   * children do not already carry one. Without this, screen reader users
   * hear nothing on focus until the tooltip renders.
   */
  label?: string;
}

export function Tooltip({
  content,
  children,
  position = "top",
  delay = 200,
  disabled = false,
  className,
  maxWidth,
  label,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  // Use a generic HTMLElement ref: we render the trigger as <span> for string
  // content but as <div> when content includes block-level JSX (MetricTooltip
  // passes a styled div), and both element types expose getBoundingClientRect.
  const triggerRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest calculatePosition reference kept outside useEffect deps so the
  // scroll/resize listeners aren't torn down and re-registered every time
  // `position` changes — that would cause a brief listener gap on orientation
  // change on mobile.
  const calcPositionRef = useRef<() => void>(() => {});

  // Stable id so the trigger can advertise the tooltip via aria-describedby.
  const tooltipId = useId();

  // Detect block-level content so the trigger element can be upgraded from
  // <span> to <div>. A <span> (inline-flex) cannot legally contain block
  // children — Chrome silently reflows, Firefox warns, and a11y trees pick
  // up the invalid nesting.
  const hasBlockContent =
    isValidElement(children) &&
    (() => {
      const type = (children as ReactElement).type;
      if (typeof type !== "string") return true; // components are opaque → assume block
      return type !== "span" && type !== "a" && type !== "button" && type !== "code";
    })();

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const gap = 8;

    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        top = triggerRect.top - tooltipRect.height - gap;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + gap;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case "left":
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.left - tooltipRect.width - gap;
        break;
      case "right":
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.right + gap;
        break;
    }

    const padding = 8;
    if (left < padding) left = padding;
    if (left + tooltipRect.width > window.innerWidth - padding) {
      left = window.innerWidth - tooltipRect.width - padding;
    }
    if (top < padding) top = padding;
    if (top + tooltipRect.height > window.innerHeight - padding) {
      top = window.innerHeight - tooltipRect.height - padding;
    }

    setCoords({ top, left });
  }, [position]);

  const clearTimers = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const show = (immediate: boolean) => {
    if (disabled) return;
    clearTimers();
    if (immediate || delay === 0) {
      setIsVisible(true);
    } else {
      showTimeoutRef.current = setTimeout(() => setIsVisible(true), delay);
    }
  };

  const hide = () => {
    clearTimers();
    // Short grace period so the user can move the cursor from trigger to
    // tooltip content (e.g. to click a link inside MetricTooltip) without
    // the tooltip snapping shut mid-traversal.
    hideTimeoutRef.current = setTimeout(() => setIsVisible(false), 80);
  };

  const handleMouseEnter = () => show(false);
  const handleMouseLeave = () => hide();
  // Focus is keyboard-driven — skip the hover delay so the tooltip feels
  // instant when tabbing through the page.
  const handleFocus = () => show(true);
  const handleBlur = () => hide();

  // Keep the latest positioning callback in a ref so the effect below only
  // registers listeners once per visibility toggle — rather than re-registering
  // every time `position` changes (which would drop scroll-position updates
  // mid-animation on mobile orientation changes).
  useEffect(() => {
    calcPositionRef.current = calculatePosition;
  }, [calculatePosition]);

  useEffect(() => {
    if (!isVisible) return;
    const onEvent = () => calcPositionRef.current();
    // Measure once the tooltip is in the DOM; before that its size is 0.
    onEvent();
    const opts: AddEventListenerOptions = { passive: true, capture: true };
    window.addEventListener("scroll", onEvent, opts);
    window.addEventListener("resize", onEvent);
    return () => {
      window.removeEventListener("scroll", onEvent, opts);
      window.removeEventListener("resize", onEvent);
    };
  }, [isVisible]);

  // ESC dismisses — WAI-ARIA tooltip pattern requires this so keyboard
  // users can collapse an open tooltip without moving focus. Skip the
  // listener entirely when disabled so we don't leak a global handler
  // for a component that can never show anything.
  useEffect(() => {
    if (!isVisible || disabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsVisible(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isVisible, disabled]);

  useEffect(() => () => clearTimers(), []);

  const isStringContent = typeof content === "string";

  // Propagate aria-describedby onto the direct child element while the
  // tooltip is enabled: screen readers announce the association on focus
  // immediately, even during the show-delay before the portal mounts.
  // Skip when `disabled` — otherwise the child advertises a description
  // that can never render, leaving AT with a dangling reference.
  // Merge with any existing aria-describedby instead of overwriting, so we
  // don't silently erase descriptions set by the consumer (form helpers,
  // validators, nested components all routinely set this attribute).
  const describedChild =
    isValidElement(children) && !disabled
      ? (() => {
          const child = children as ReactElement<HTMLAttributes<HTMLElement>>;
          const existing = child.props["aria-describedby"];
          const merged = existing ? `${existing} ${tooltipId}` : tooltipId;
          return cloneElement(child, { "aria-describedby": merged });
        })()
      : children;

  // Use a <div> wrapper when the child is block-level content so we don't
  // emit invalid HTML (<span> wrapping a block element). Default to <span>
  // for the common icon/text case so layout flow is preserved.
  const TriggerTag = hasBlockContent ? "div" : "span";

  return (
    <>
      <TriggerTag
        // Both `span` and `div` produce an HTMLElement ref at runtime. A ref
        // callback works for the dynamic `"div" | "span"` tag where a plain
        // RefObject does not (JSX widens the element type to HTMLDivElement
        // for the first union member). `el` is always an HTMLElement subtype.
        ref={(el: HTMLElement | null) => { triggerRef.current = el; }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-label={label}
        className={className ?? "inline-flex"}
      >
        {describedChild}
      </TriggerTag>

      {isVisible &&
        !disabled &&
        createPortal(
          <div
            id={tooltipId}
            ref={tooltipRef}
            role="tooltip"
            style={{
              position: "fixed",
              // Hide until measured so the first paint doesn't flash at (0,0).
              top: coords ? `${coords.top}px` : "-9999px",
              left: coords ? `${coords.left}px` : "-9999px",
              visibility: coords ? "visible" : "hidden",
              zIndex: 9999,
              ...(maxWidth ? { maxWidth: `${maxWidth}px` } : {}),
            }}
            className={`adp-tooltip animate-fade-in motion-reduce:animate-none${isStringContent ? " uppercase tracking-[0.05em] whitespace-nowrap" : ""}`}
            onMouseEnter={() => clearTimers()}
            onMouseLeave={hide}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}

// Icon tooltip — für Info-Icons
interface InfoTooltipProps {
  content: string | ReactNode;
  iconSize?: number;
  position?: "top" | "bottom" | "left" | "right";
  /**
   * Accessible name for the trigger. Screen readers hear this on focus
   * before the tooltip renders — defaults to a generic label if the caller
   * does not know a better one for this context.
   */
  label?: string;
}

export function InfoTooltip({
  content,
  iconSize = 14,
  position = "top",
  label,
}: InfoTooltipProps) {
  // Prefer a string-content label so "What is X?" screen-reader prompts
  // are context-aware. Falls back to the generic phrasing when the
  // tooltip contains rich JSX the caller did not summarize.
  const stringContent = typeof content === "string" ? content : null;
  const derivedLabel =
    label ??
    (stringContent
      ? stringContent.length > MAX_ARIA_LABEL_LEN
        ? `More information: ${stringContent.slice(0, MAX_ARIA_LABEL_LEN).trimEnd()}…`
        : `More information: ${stringContent}`
      : "More information");

  return (
    <Tooltip content={content} position={position}>
      <button
        type="button"
        className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-2 transition-colors motion-reduce:transition-none cursor-help"
        aria-label={derivedLabel}
      >
        <HelpCircle style={{ width: iconSize, height: iconSize }} aria-hidden="true" />
      </button>
    </Tooltip>
  );
}

// Metric tooltip — für Dashboard-Metriken mit Label, Beschreibung und Formel
interface MetricTooltipProps {
  label: string;
  description: string;
  formula?: string;
  children: ReactNode;
}

export function MetricTooltip({ label, description, formula, children }: MetricTooltipProps) {
  const content = (
    <div className="space-y-2">
      <div>
        <div className="font-semibold text-foreground mb-1">{label}</div>
        <div className="text-muted-foreground leading-relaxed">{description}</div>
      </div>
      {formula && (
        <div className="pt-2 border-t border-border">
          <div className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider mb-1">
            Formula
          </div>
          <code className="text-[11px] text-mono text-foreground">{formula}</code>
        </div>
      )}
    </div>
  );

  return (
    <Tooltip content={content} maxWidth={350} position="bottom" label={label}>
      {children}
    </Tooltip>
  );
}

// ── Radix compound-tooltip primitives (shadcn convention) ───────────────────
// The custom `Tooltip` above uses a `content` prop and is the dashboard's
// portable tooltip. The shadcn-generated `ui/sidebar.tsx` instead expects the
// Radix compound API (Provider / Root / Trigger / Content). These thin
// wrappers supply exactly that surface without touching the custom component.

export const TooltipProvider = TooltipPrimitive.Provider;

/** Radix tooltip root — exported as `TooltipRoot` to avoid clashing with the
 *  custom `Tooltip` export consumed across the dashboard. */
export const TooltipRoot = TooltipPrimitive.Root;

export const TooltipTrigger = TooltipPrimitive.Trigger;

export function TooltipContent({
  className,
  sideOffset = 4,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        sideOffset={sideOffset}
        className={cn("adp-tooltip animate-fade-in motion-reduce:animate-none z-[9999]", className)}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
}
